import { z } from "zod";

import { now } from "../../lib/clock.ts";

import type { Filter, PredecisionOutcome } from "./api/contract.ts";
import { MONTH_NAMES } from "./texts.ts";

/**
 * La cola y el documento abierto, EN LA URL.
 *
 * Lo que decide qué se ve --el buscador, la pestaña de la cola y la factura
 * abierta-- viaja en los search params. Así un link comparte la
 * vista y recargar no la pierde. Este archivo es el único que convierte esos
 * params en lo que se le pide al API.
 */

/**
 * El router lee como número lo que parece un número (`?q=2026`, `?f=746868`)
 * y como booleano `true`.
 *
 * La factura abierta y el buscador se dejan como lleguen --número o texto--
 * y se leen con `textOf`: si el esquema los devolviera como texto, el router
 * los escribiría entre comillas para que no parezcan números, y la URL
 * quedaría `?f=%22746868%22`.
 */
const loose = z.union([z.string(), z.number()]).optional().catch(undefined);

/** Lo que vino como número o texto, como texto. */
export const textOf = (
  value: string | number | undefined,
): string | undefined => (value === undefined ? undefined : String(value));

/**
 * Las pestañas de la lista, por lo que propone el sistema para sus facturas
 * pendientes. La primera es la de partida. Solo GUÍA: la decisión es del
 * digitador.
 */
export const TABS = [
  { value: "pre-aprobadas", outcome: "pre_approved" },
  { value: "pre-rechazadas", outcome: "pre_rejected" },
  { value: "por-digitar", outcome: "needs_review" },
] as const satisfies ReadonlyArray<{
  value: string;
  outcome: PredecisionOutcome;
}>;

export type TabValue = (typeof TABS)[number]["value"];

export const DEFAULT_TAB: TabValue = "pre-aprobadas";

export const digitationSearchSchema = z.object({
  /** La factura abierta, por su identificador. Se lee con `textOf`. */
  f: loose,
  /** Lo escrito en el buscador de la cola. Se lee con `textOf`. */
  q: loose,
  /** La pestaña; sin ella, «Pre-aprobadas». */
  tab: z
    .enum(["pre-aprobadas", "pre-rechazadas", "por-digitar"])
    .optional()
    .catch(undefined),
  /** El mes de carga, `AAAA-MM`. Sin él, el mes en curso. */
  mes: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
});

export type DigitationSearch = z.infer<typeof digitationSearchSchema>;

/** La pestaña de la URL, con la de partida si no hay. */
export const tabOf = (search: DigitationSearch) =>
  TABS.find((tab) => tab.value === search.tab) ?? TABS[0];

/** El mes en curso, `AAAA-MM`, en la hora local de quien digita. */
export const currentMonth = (date: Date = now()): string =>
  `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, "0")}`;

/** `2026-10` → «octubre 2026». */
export const monthLabel = (month: string): string => {
  const [year = "", number = ""] = month.split("-");
  return `${MONTH_NAMES[Number(number) - 1] ?? number} ${year}`;
};

/** El mes de carga con el que se trabaja: el de la URL o el en curso. */
export const monthOf = (search: DigitationSearch): string =>
  search.mes ?? currentMonth();

/**
 * Las facturas que esperan a un digitador: la cola con la que se trabaja y
 * con la que «Tomar siguiente» se queda dentro de la pestaña y la búsqueda.
 */
export const pendingFilters = (
  uploadMonth: string,
  predecision?: PredecisionOutcome,
): Filter[] => [
  { field: "uploadMonth", operator: "eq", value: uploadMonth },
  { field: "status", operator: "eq", value: "waiting" },
  { field: "stepKey", operator: "eq", value: "human-review" },
  ...(predecision === undefined
    ? []
    : [{ field: "predecision", operator: "eq", value: predecision } as const]),
];
